import React, { useState, useEffect } from 'react'
import {
  KTNDetails,
  KTNDetailsStatusEnum,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import PageContent from '@components/layout/PageContent'
import { HeadTitle } from '@components/layout/HeadTitle'
import { PageHeading, CardHeading } from '@components/layout/Heading'
import { Card, Flex, Text, Link } from '@clear/design-system'
import { Form, Row } from '@components/layout/Form'
import { StatusCardDetails } from '../status/StatusCardDetails'
import dateToString from '@utils/dateToString'
import { getDisplayName } from '@utils/displayName'
import NextLink from 'next/link'

interface KtnCardProps {
  data: KTNDetails
}

const KtnCard: React.FC<KtnCardProps> = ({ data }) => {
  const [ktnDescription, setKtnDescription] = useState(null)
  const [hideKtnDetails, setHideKtnDetails] = useState<boolean>(false)
  const displayExpirationDate = dateToString(data.expirationDate)
  const displayName = getDisplayName(data.name)

  useEffect(() => {
    setHideKtnDetails(!data.ktn || !data.expirationDate)
    if (data.status === KTNDetailsStatusEnum.INPROGRESS) {
      setKtnDescription(
        <Row mx={[2, 6]} data-testid="ktn-card-in-progress">
          <Text
            variant="body"
            textAlign="center"
            color="neutralsTextGray"
            margin="auto"
          >
            Check the status of your current application{' '}
            <NextLink href="/status" passHref>
              <Link>here.</Link>
            </NextLink>
          </Text>
        </Row>
      )
    } else if (data.status === KTNDetailsStatusEnum.NOTELIGIBLEFORRENEWAL) {
      setKtnDescription(
        <Row mx={[2, 6]} data-testid="ktn-card-not-eligible-for-renewal">
          <Text variant="body" textAlign="center" color="neutralsTextGray">
            Your Known Traveler Number (KTN) is valid until the expiration date
            above. You may renew your TSA PreCheck® membership online up to 6
            months before your membership expiration date.
          </Text>
        </Row>
      )
    } else if (data.status === KTNDetailsStatusEnum.ELIGIBLEFORRENEWAL) {
      setKtnDescription(
        <Row mx={[2, 6]} data-testid="ktn-card-eligible-for-renewal">
          <Text
            variant="body"
            textAlign="center"
            color="neutralsTextGray"
            margin="auto"
          >
            You’re eligible for renewal - Complete your renewal online{' '}
            <NextLink href="/renewal/eligibility/eligibility" passHref>
              <Link>here.</Link>
            </NextLink>
          </Text>
        </Row>
      )
    } else if (data.status === KTNDetailsStatusEnum.COMPLETED) {
      setKtnDescription(
        <>
          <Row data-testid="ktn-card-completed">
            <Text
              variant="body"
              textAlign="center"
              color="neutralsTextGray"
              mx={[2, 6]}
              p={[3, 6]}
            >
              Your Known Traveler Number (KTN) has been issued. Known Traveler
              Numbers (KTNs) are automatically generated to accommodate personal
              preferences.
            </Text>
          </Row>
          <Row mx={[2, 6]}>
            <Text variant="body" textAlign="center" color="neutralsTextGray">
              For information on how to use your KTN please see this “How do I
              use my Known Traveler Number?” FAQ{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck/faq"
                target="_blank"
              >
                here
              </Link>
              .
            </Text>
          </Row>
        </>
      )
    }
  }, [data])

  return (
    <PageContent>
      <HeadTitle
        title="What is my KTN? | TSA&nbsp;PreCheck® Known Traveler Number Look Up"
        description="If you are a TSA&nbsp;PreCheck® member, you can look up your known traveler number online. Find your KTN here."
      />
      <PageHeading>Look Up KTN</PageHeading>
      <Card width="full" data-cy="ktn-card">
        <Flex flexDirection="column">
          <Form>
            <Row alignItems="center">
              <CardHeading>{displayName}</CardHeading>
            </Row>
            {!hideKtnDetails ? (
              <StatusCardDetails
                content1="Known Traveler Number (KTN)"
                value1={data.ktn}
                content2="Expiration Date"
                value2={displayExpirationDate}
              />
            ) : null}
            {ktnDescription}
          </Form>
        </Flex>
      </Card>
    </PageContent>
  )
}

export default KtnCard
